import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, X, PlusCircle } from 'lucide-react';
import { SEL_API } from '../../lib/selectionShare';

const pad = (n) => String(n).padStart(2, '0');
const WD = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
// Days of the Italian schedule (Europe/Rome), whatever the phone's time zone
const romeToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date());
const dayOptions = () => {
  const [y, m, d0] = romeToday().split('-').map(Number);
  return [['', 'Tutti i giorni'], ...Array.from({ length: 11 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1, d0 + i));
    const iso = d.toISOString().slice(0, 10);
    return [iso, i === 0 ? 'Oggi' : i === 1 ? 'Domani' : `${WD[d.getUTCDay()]} ${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}`];
  })];
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '');

const Result = ({ m, onPick }) => (
  <button type="button" onClick={() => onPick(m)} className="w-full text-left py-3 flex items-center gap-3" data-testid={`match-search-result-${m.fixture_id}`}>
    <span className="flex-1 min-w-0">
      <span className="flex justify-between gap-2 text-[11px] text-gray-500">
        <span className="truncate">{m.league_name}</span>
        <span className="shrink-0">{fmtDate(m.match_date)} {m.match_time}</span>
      </span>
      <span className="block text-white font-bold text-sm truncate">{m.home_team} - {m.away_team}</span>
    </span>
    <PlusCircle className="w-6 h-6 text-[#F5A623] shrink-0" />
  </button>
);

// "Aggiungi partita": day filter + team search over the app schedule (today … +10 days)
export const MatchSearch = ({ onPick, onClose, initial = '' }) => {
  const days = useMemo(dayOptions, []);
  const [q, setQ] = useState(initial);
  const [day, setDay] = useState('');
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!day && q.trim().length < 2) { setRes([]); return undefined; }
    const t = setTimeout(() => {
      setBusy(true);
      axios.get(`${SEL_API}/search`, { params: { q: q.trim(), day } }).then((r) => setRes(r.data || [])).catch(() => setRes([])).finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q, day]);
  return (
    <div className="fixed inset-0 z-[9995] bg-[#121212] flex flex-col" data-testid="match-search">
      <div className="max-w-md w-full mx-auto flex flex-col h-full px-4 pt-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-white font-black text-lg">Aggiungi partita</p>
          <button type="button" onClick={onClose} className="p-1 text-gray-400" data-testid="match-search-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-2 -mx-4 px-4" data-testid="match-search-days">
          {days.map(([v, l]) => (
            <button key={v || 'all'} type="button" onClick={() => setDay(v)} data-testid={`match-search-day-${v || 'all'}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${day === v ? 'bg-[#F5A623] text-[#1A1A1A] border-[#F5A623]' : 'border-white/15 text-gray-300'}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-[#1A1A1A] border border-white/10 rounded-xl px-3 mb-2">
          <Search className="w-4 h-4 text-gray-500" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome squadra" className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" data-testid="match-search-input" />
          {busy && <Loader2 className="w-4 h-4 animate-spin text-[#F5A623]" />}
          {q && <button type="button" onClick={() => setQ('')} className="text-gray-500" data-testid="match-search-clear"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-white/5 pb-[max(env(safe-area-inset-bottom,0px),1rem)]">
          {res.map((m) => <Result key={m.fixture_id} m={m} onPick={onPick} />)}
          {!day && q.trim().length < 2 && <p className="text-xs text-gray-500 py-3" data-testid="match-search-hint">Scegli un giorno o scrivi il nome di una squadra.</p>}
          {(day || q.trim().length >= 2) && !busy && !res.length && <p className="text-xs text-gray-400 py-3" data-testid="match-search-empty">Nessuna partita trovata.</p>}
        </div>
      </div>
    </div>
  );
};

export default MatchSearch;
